import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { SubscriptionService } from "@/services/billing/SubscriptionService";

export const useCallPurchases = () => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const {
    data: callPurchases = [],
    isLoading: isLoadingPurchases,
    refetch: refetchPurchases,
  } = useQuery({
    queryKey: ["callPurchases"],
    queryFn: () => SubscriptionService.getCallPurchases(),
    enabled: !!user?.id,
  });

  const purchaseMutation = useMutation({
    mutationFn: (params: { agentId: string; quantity: number }) =>
      SubscriptionService.purchaseAgentCalls(params.agentId, params.quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callPurchases"] });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (params: { agentId: string; quantity?: number }) =>
      SubscriptionService.cancelAgentCalls(params.agentId, params.quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callPurchases"] });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const purchaseCalls = useCallback(
    async (agentId: string, quantity: number) => {
      return purchaseMutation.mutateAsync({ agentId, quantity });
    },
    [purchaseMutation],
  );

  const cancelCalls = useCallback(
    async (agentId: string, quantity?: number) => {
      return cancelMutation.mutateAsync({ agentId, quantity });
    },
    [cancelMutation],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    callPurchases,
    isLoadingPurchases,
    refetchPurchases,
    purchaseCalls,
    cancelCalls,
    isPurchasing: purchaseMutation.isPending,
    isCanceling: cancelMutation.isPending,
    error,
    clearError,
  };
};
